import axios, { AxiosError, AxiosResponse } from "axios";
import { toast } from "react-toastify";
import { router } from "../router/Routes";
import { LoginRequest } from "../models/LoginRequest";
import { LoginResponse } from "../models/LoginResponse";
import { ApiResponse } from "../models/ApiResponse";
import { store } from "../stores/store";
import { baseApiUrl } from "./constants";

axios.defaults.baseURL = baseApiUrl;

const responseBody = <T>(response: AxiosResponse<T>) => response.data;

axios.interceptors.request.use((config) => {
  const token = store.userStore.token;
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axios.interceptors.response.use(
  async (response) => {
    return response;
  },
  (error: AxiosError) => {
    const { data, status, config } = error.response as AxiosResponse;
    switch (status) {
      case 400:
        if (config.method === "get" && data.errors.hasOwnProperty("id")) {
          router.navigate("/not-found");
        }
        if (data.errors) {
          const modalStateErrors = [];
          for (const key in data.errors) {
            if (data.errors[key]) {
              modalStateErrors.push(data.errors[key]);
            }
          }
          throw modalStateErrors.flat();
        } else {
          toast.error(data);
        }
        break;
      case 401:
        toast.error("unauthorised");
        break;
      case 403:
        toast.error("forbidden");
        break;
      case 404:
        router.navigate("/not-found");
        break;
      case 500:
        console.log(error);
        break;
    }
    return Promise.reject(error);
  }
);

const requests = {
  get: <T>(url: string) => axios.get<T>(url).then(responseBody),
  post: <T>(url: string, body: {}) =>
    axios.post<T>(url, body).then(responseBody),
  put: <T>(url: string, body: {}) => axios.put<T>(url, body).then(responseBody),
  del: <T>(url: string) => axios.delete<T>(url).then(responseBody),
  patch: <T>(url: string, body?: {}) =>
    axios.patch<T>(url, body).then(responseBody),
  delete: <T>(url: string) => axios.delete<T>(url).then(responseBody),
};

const Product = {
  getAll: (
    filters: {
      category_id?: number;
      sub_category_id?: number;
      name?: string;
      org_id?: number;
    } = {}
  ) => requests.post("/product", filters),
  getProduct: (id: number) => requests.get(`product/${id}`),
  updateStatus: (productId: number) => requests.patch(`/product/${productId}`),
  delete: (productId: number) => requests.delete(`/product/${productId}`),
  createProduct: (data: FormData) => requests.post("/product/create", data),
  updateProduct: (data: FormData, id: number) =>
    requests.patch(`product/update/${id}`, data),

  productOrganisationMap: (data: any) =>
    axios.post("/product-mapper/attach-to-org", data, {
      headers: {
        "Content-Type": "application/json",
      },
    }),
  updateProductMapper: (id: number, data: any) =>
    axios.patch(`/product-mapper/update/${id}`, data),

  productOrganisationUnMap: (data: any) =>
    axios.post("/product-mapper/remove-attached-products", data, {
      headers: {
        "Content-Type": "application/json",
      },
    }),

    uploadProduct: (data: FormData) =>
    axios.post("/product/upload-excel", data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }),
};

const Customer = {};

const User = {
  login: (credentials: LoginRequest) =>
    requests.post<ApiResponse<LoginResponse>>("/auth/login", credentials),
  current: () => requests.get("/auth/profile"),
  getUserRole: () => requests.get<ApiResponse<LoginResponse>>("/auth/roles"),
};

const Category = {
  get: () => requests.get<ApiResponse<LoginResponse>>("/category"),
  delete: (id: number) => axios.delete(`/category/${id}`),
  create: (data: FormData) =>
    axios.post("/category/create", data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }),
  update: (data: FormData) => requests.post("/category", data),
  getById: (id: number) => requests.get(`/category/${id}`),
  updateStatus: (id: number) => requests.patch(`/category/${id}`),
  uploadCategory: (data: FormData) =>
    axios.post("/category/create-by-file", data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }),
};

const Orders = {
  get: (config = {}) =>
    axios.post<ApiResponse<any>>("/orders", config).then(responseBody),
  getstatus: () => requests.get<ApiResponse<LoginResponse>>("/orders/status"),
  delete: (id: number) => axios.delete(`/orders/${id}`),
  create: (data: FormData) =>
    axios.post("/orders/create", data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }),
  update: (data: FormData) => requests.post("/orders", data),
  getById: (id: number) => requests.get(`/orders/${id}`),
  changeStatus: (payload: { order_id: string; status_id: number }) =>
    requests.post<ApiResponse<any>>("/orders/change-status", payload),

  updatequantity: (data: {
    order_id: number;
    product_id: number;
    quantity: number;
  }) => requests.post("orders/update-quantity", data),

  getSalesReturnReason:() => requests.get<ApiResponse<LoginResponse>>(`/sales-return/reasons/`),

  createSalesReturn: (data: FormData) =>
    axios.post("/sales-return/create", data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }),

    getSalesReturn: (config = {}) =>
    axios.get<ApiResponse<any>>("/sales-return/all", config).then(responseBody),

    getPOD: (config = {}) =>
    axios.get<ApiResponse<any>>("/pod/all", config).then(responseBody),

    createCreditNote: (data: any) =>
    axios.post("/credit-note/create", data, {
      headers: {
        "Content-Type": "application/json",
      },
    }),

    getPODById: (id: number) => requests.get(`/pod/${id}`),

    getCN: (config = {}) =>
    axios.get<ApiResponse<any>>("/credit-note/all", config).then(responseBody),


  
};

const Organisation = {
  get: (
    filters: {
      category_id?: number;
      sub_category_id?: number;
      name?: string;
      org_id?: number;
    } = {}
  ) => requests.post("/organisation", filters),
  delete: (id: number) => axios.delete(`/organisation/${id}`),
  create: (data: any) =>
    axios.post("/organisation/create", data, {
      headers: {
        "Content-Type": "application/json",
      },
    }),
  getById: (id: number) => requests.get(`/organisation/${id}`),

  updateOrganisation: (data: FormData, id: number) =>
    requests.patch(`organisation/update/${id}`, data),
};

const Outlet = {
  get: (config = {}) =>
    axios.get<ApiResponse<any>>("/outlet", config).then(responseBody),
  delete: (id: number) => axios.delete(`/outlet/${id}`),
  create: (data: any) =>
    axios.post("/outlet", data, {
      headers: {
        "Content-Type": "application/json",
      },
    }),
  getByOrganisation: (org_id: number) =>
    requests.get<ApiResponse<any>>(`/outlet/by-org?org_id=${org_id}`),
  getById: (id: number) => requests.get(`/outlet/${id}`),
  updateOutlet: (data: FormData, id: number) =>
    requests.patch(`outlet/update/${id}`, data),
};

const OutletUser = {
  getUser: () =>
    axios.get<ApiResponse<any>>("/outlet-user/get").then(responseBody),

  createuser: (data: any) =>
    axios.post("/outlet-user/create", data, {
      headers: {
        "Content-Type": "application/json",
      },
    }),
    getById: (id: number) => requests.get(`/outlet-user/get/${id}`),
  updateUser: (data: FormData, id: number) =>
    requests.patch(`outlet-user/update/${id}`, data),
};

const SubCategory = {
  get: () => requests.get<ApiResponse<LoginResponse>>("/sub-category"),
  create: (data: FormData) => requests.post("/sub-category/create", data),
  delete: (id: number) => axios.delete(`/sub-category/${id}`),
  update: (data: FormData, id: number) =>
    requests.put(`/sub-category/update/${id}`, data),
  getById: (id: number) => requests.get(`/sub-category/${id}`),
  updateStatus: (id: number) => requests.patch(`/sub-category/${id}`),

  uploadSubCategory: (data: FormData) =>
    axios.post("/sub-category/create-by-file", data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }),

};
const States = {
  get: () => requests.get<ApiResponse<LoginResponse>>("/states"),
};

const Cities = {
  getByState: (stateId: number) =>
    requests.get<ApiResponse<any>>(`/cities?state_id=${stateId}`),
};

const agent = {
  Product,
  Customer,
  User,
  Category,
  Orders,
  SubCategory,
  Organisation,
  Outlet,
  OutletUser,
  States,
  Cities,
};

export default agent;
